import {
  Icon,
  IconName,
  IconSize,
  IconVariant,
  PathCompositionSize,
  PopoverPlacement,
  ResourceStatus,
  Searchable,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useLinkAttrs
} from "@quortex/front-common"
import { useSuspenseQuery } from "@tanstack/react-query"
import type { FC } from "react"
import { useDeleteOttEndpoint } from "src/queries/playOttEndpoints"
import { usePool } from "src/queries/playPools"
import OttEndpointActionMenu from "@/App/play/App/Main/Pool/endpoints/ottEndpoints/OttEndpointActionMenu/OttEndpointActionMenu"
import PlayPathComposition from "@/App/play/components/features/endpoints/ottEndpoints/PlayPathComposition"
import type { RichOttEndpoint } from "./types"

interface Props {
  endpoints: RichOttEndpoint[]
  searchRegExps: RegExp[]
  prettySearchRegExps: RegExp[]
}

const OttEndpointTable: FC<Props> = ({ endpoints, searchRegExps, prettySearchRegExps }) => {
  return (
    <Table onClick={e => e.stopPropagation()}>
      <THead className="!bg-white">
        <TR>
          <TH>Processing</TH>
          <TH>Packaging</TH>
          <TH>Path</TH>
          <TH className="w-[10rem]">Status</TH>
          <TH className="w-0" />
        </TR>
      </THead>
      <TBody>
        {endpoints.map(endpoint => (
          <Row
            key={endpoint.uuid}
            endpoint={endpoint}
            prettySearchRegExps={prettySearchRegExps}
            searchRegExps={searchRegExps}
          />
        ))}
      </TBody>
    </Table>
  )
}

interface RowProps {
  endpoint: RichOttEndpoint
  searchRegExps: RegExp[]
  prettySearchRegExps: RegExp[]
}

const Row = ({ endpoint, searchRegExps, prettySearchRegExps }: RowProps) => {
  const { data: pool } = useSuspenseQuery(usePool())
  useDeleteOttEndpoint({ ottEndpointUuid: endpoint.uuid })

  const linkAttrs = useLinkAttrs({
    pathname: `${endpoint.uuid}/overview`,
    search: back => ({ back })
  })

  return (
    <TR key={endpoint.uuid} className="whitespace-nowrap" {...linkAttrs}>
      <TD>
        <div className="flex items-center gap-xs">
          <Icon icon={IconName.PROCESSING} size={IconSize.SMALL} type={IconVariant.SOLID} />
          <Searchable searchRegExps={searchRegExps} value={endpoint.processing?.name} />
        </div>
      </TD>
      <TD>
        <div className="flex items-center gap-xs">
          <Icon icon={IconName.PACKAGING} size={IconSize.SMALL} type={IconVariant.SOLID} />
          <Searchable searchRegExps={searchRegExps} value={endpoint.target?.name} />
        </div>
      </TD>
      <TD>
        <PlayPathComposition
          disabled={endpoint.status === "disabled"}
          endpoint={endpoint}
          processingType={pool.type}
          searchRegExps={prettySearchRegExps}
          size={PathCompositionSize.XSMALL}
        />
      </TD>
      <TD>
        <ResourceStatus
          messageDetail={endpoint.statusMessages || []}
          popoverPlacement={PopoverPlacement.BOTTOM}
          status={endpoint.status}
          variant="status"
        />
      </TD>
      <TD>
        <OttEndpointActionMenu endpoint={endpoint} />
      </TD>
    </TR>
  )
}

export default OttEndpointTable
